<script setup lang="ts">
/**
 * Friendly modal that appears whenever the read-only demo workspace blocks a
 * mutation (server returns 403 demo_readonly, axios interceptor dispatches
 * `vc:demo-readonly`). Mounted at the App root so it works across all views.
 *
 * Throttled to once per 8 seconds — clicking many disabled actions in a row
 * would otherwise flood the modal and feel hostile.
 */
import { ref, onMounted, onBeforeUnmount } from 'vue';

const open = ref(false);
let lastShown = 0;
const THROTTLE_MS = 8000;

function show() {
  const now = Date.now();
  if (now - lastShown < THROTTLE_MS) return;
  lastShown = now;
  open.value = true;
  // Lock body scroll while open (mirrors BotsView modal pattern)
  document.body.style.overflow = 'hidden';
}

function close() {
  open.value = false;
  document.body.style.overflow = '';
}

function onEsc(e: KeyboardEvent) {
  if (e.key === 'Escape' && open.value) close();
}

const onDemoReadonly = () => show();

onMounted(() => {
  window.addEventListener('vc:demo-readonly', onDemoReadonly);
  window.addEventListener('keydown', onEsc);
});
onBeforeUnmount(() => {
  window.removeEventListener('vc:demo-readonly', onDemoReadonly);
  window.removeEventListener('keydown', onEsc);
  document.body.style.overflow = '';
});

const APP_BASE = (import.meta.env.VITE_APP_URL as string | undefined) || '';
function signupUrl() {
  return APP_BASE ? `${APP_BASE}/signup` : '/signup';
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="vc-demo-overlay"
      role="dialog"
      aria-modal="true"
      :aria-label="$t('demo.modal.title')"
      @click.self="close"
    >
      <div class="vc-demo-modal">
        <button type="button" class="vc-demo-modal-close" :aria-label="$t('common.close')" @click="close">×</button>

        <div class="vc-demo-modal-icon">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 12l2 2 4-4" />
            <circle cx="12" cy="12" r="10" />
          </svg>
        </div>

        <h2 class="vc-demo-modal-title">{{ $t('demo.modal.title') }}</h2>
        <p class="vc-demo-modal-body">{{ $t('demo.modal.body') }}</p>

        <ul class="vc-demo-modal-list">
          <li>{{ $t('demo.modal.feature1') }}</li>
          <li>{{ $t('demo.modal.feature2') }}</li>
          <li>{{ $t('demo.modal.feature3') }}</li>
        </ul>

        <div class="vc-demo-modal-actions">
          <a :href="signupUrl()" class="vc-btn vc-btn--primary vc-demo-modal-cta">
            {{ $t('demo.modal.signupCta') }}
          </a>
          <button type="button" class="vc-btn vc-btn--ghost" @click="close">
            {{ $t('demo.modal.keepLooking') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.vc-demo-overlay {
  position: fixed;
  inset: 0;
  z-index: 250;       /* above modals (200) so it always wins */
  background: rgba(16, 20, 117, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: vc-demo-fade 180ms ease;
}
@keyframes vc-demo-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}
.vc-demo-modal {
  position: relative;
  background: var(--vc-white);
  border-radius: var(--vc-radius);
  padding: 36px 36px 28px;
  max-width: 460px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 50px rgba(16, 20, 117, 0.25);
  animation: vc-demo-pop 180ms cubic-bezier(.34,1.56,.64,1);
}
@keyframes vc-demo-pop {
  from { transform: scale(0.94) translateY(8px); opacity: 0; }
  to { transform: scale(1) translateY(0); opacity: 1; }
}
.vc-demo-modal-close {
  position: absolute;
  top: 8px; right: 12px;
  background: transparent;
  border: none;
  font-size: 28px;
  line-height: 1;
  color: var(--vc-text-muted);
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 6px;
  transition: background 120ms ease;
}
.vc-demo-modal-close:hover { background: var(--vc-bg); color: var(--vc-text); }
.vc-demo-modal-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px; height: 64px;
  background: rgba(36, 191, 179, 0.12);
  color: var(--vc-teal);
  border-radius: 50%;
  margin-bottom: 14px;
}
.vc-demo-modal-title {
  margin: 0 0 8px;
  font-size: 19px;
  font-weight: 700;
  color: var(--vc-text);
}
.vc-demo-modal-body {
  margin: 0 0 16px;
  font-size: 14.5px;
  line-height: 1.55;
  color: var(--vc-text-secondary);
}
.vc-demo-modal-list {
  list-style: none;
  padding: 0;
  margin: 0 0 22px;
  text-align: left;
  font-size: 14px;
  color: var(--vc-text);
}
.vc-demo-modal-list li {
  position: relative;
  padding: 6px 0 6px 26px;
  line-height: 1.4;
}
.vc-demo-modal-list li::before {
  content: '✓';
  position: absolute;
  left: 4px;
  top: 6px;
  color: var(--vc-teal);
  font-weight: 700;
}
.vc-demo-modal-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
}
.vc-demo-modal-cta {
  text-decoration: none;
  min-width: 160px;
}
@media (max-width: 480px) {
  .vc-demo-modal { padding: 28px 22px 22px; }
  .vc-demo-modal-actions { flex-direction: column; }
  .vc-demo-modal-actions .vc-btn { width: 100%; }
}
</style>
